import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import message from 'antd/es/message';

import { Button, Modal } from '@/components';
import { useCurrentUserId, shorten } from '@/utils';
import { setUserBlocked, useMarkUserSpam } from '@/components/UserDeal/SpamReport/hooks';
import { useHiUserCache } from '@/hooks';

import styles from './index.module.scss';

const { CommonModal } = Modal;

const ConfirmModal = ({
  on,
  turnOff,
  title,
  content,
  confirmText,
  onConfirm,
  onCancel,
}: {
  on?: boolean;
  turnOff: () => void;
  title: string;
  content: string | React.ReactElement;
  confirmText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
}) => {
  const { t } = useTranslation();

  const handleConfirm = useCallback(() => {
    onConfirm?.();
    turnOff();
  }, [onConfirm, turnOff]);

  const handleCancel = useCallback(() => {
    onCancel?.();
    turnOff();
  }, [onCancel, turnOff]);

  return (
    <CommonModal on={on} turnOff={turnOff}>
      <div className={styles.modalTitle}>{title}</div>
      <div className={styles.modalContent}>{content}</div>
      <div className={styles.operates}>
        <Button className={styles.modalBtn} onClick={handleCancel}>
          {t('FeedMoreAction_ConfirmModal.x9d2fu')}
        </Button>
        <Button className={styles.modalBtn} onClick={handleConfirm} type="primary">
          {confirmText || t('UidSetting_MintIdModal.179d1nk')}
        </Button>
      </div>
    </CommonModal>
  );
};

export const HidePostModal = ({
  on,
  turnOff,
  onConfirm,
}: {
  on?: boolean;
  turnOff: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <ConfirmModal
      on={on}
      turnOff={turnOff}
      title={t('FeedMoreAction_ConfirmModal.138zn59')}
      content={t('FeedMoreAction_ConfirmModal.hideTIp')}
      onConfirm={onConfirm}
    />
  );
};

export const MuteModal = ({
  on,
  turnOff,
  author,
  onConfirm,
}: {
  on?: boolean;
  turnOff: () => void;
  author?: string;
  onConfirm: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <ConfirmModal
      on={on}
      turnOff={turnOff}
      title={`${t('FeedMoreAction_ConfirmModal.mute')} ${author}`}
      content={t('FeedMoreAction_ConfirmModal.muteTip')}
      onConfirm={onConfirm}
      confirmText={t('FeedMoreAction_ConfirmModal.mute')}
    />
  );
};

export const BlockModal = ({
  on,
  turnOff,
  isBlocked,
  userId,
  userName,
}: {
  on?: boolean;
  turnOff: () => void;
  isBlocked?: boolean | null;
  userId: string;
  userName?: string;
}) => {
  const { t } = useTranslation();

  const currentId = useCurrentUserId();
  const { updateHiUserCache, userDict } = useHiUserCache();

  const { mutateAsync } = useMarkUserSpam();

  const title = useMemo(
    () =>
      `${isBlocked ? t('FeedMoreAction.unblock') : t('FeedMoreAction.block')} ${
        userName || shorten(userId, 6, 4)
      }`,
    [userName, userId, isBlocked, t],
  );
  const content = useMemo(
    () => (
      <div className={styles.blockDesc}>
        {isBlocked
          ? t('FeedMoreAction_ConfirmModal.9rc8be')
          : t('FeedMoreAction_ConfirmModal.nk97mg')}
      </div>
    ),
    [isBlocked, t],
  );

  const handleConfirm = useCallback(async () => {
    if (!userId || !currentId) {
      return;
    }

    await mutateAsync({
      id: userId,
      isSpam: !isBlocked,
    });
    setUserBlocked({ currentId, userId });
    updateHiUserCache({
      [userId]: {
        ...userDict[userId],
        desc: {
          ...(userDict[userId] as UserInfo)?.desc,
          is_block: !isBlocked,
        },
      } as UserInfo,
    });

    message.success('Success');
  }, [userId, currentId, mutateAsync, isBlocked, updateHiUserCache, userDict]);

  return (
    <ConfirmModal
      on={on}
      turnOff={turnOff}
      title={title}
      content={content}
      onConfirm={handleConfirm}
    />
  );
};
